import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RotateCcw, Trash2 } from 'lucide-react';
import type { AssetDetail, AssetSummary } from '@prism/contracts';
import { ApiError, errorMessage } from '../api/client';
import { useDeleteActions, useGenealogy, useMe, useRecycleSettings } from '../api/hooks';
import { usedInText } from '../pages/genealogy';
import { dateTime } from '../lib/format';
import { Modal } from './Modal';
import { toast } from './Toasts';

type Deletable = AssetSummary & Partial<Pick<AssetDetail, 'representations'>>;

/** What a purge will do to this asset's files, in plain words. */
function consequences(asset: Deletable): string[] {
  const lines = ['Proxies, thumbnails, scrub images and waveforms are removed.'];
  const original = asset.representations?.find((r) => r.kind === 'original');
  if (!original) {
    lines.push('Originals PRISM stored from uploads are deleted. Files catalogued in place stay where they are.');
    return lines;
  }
  const inPlace = original.locations.filter((l) => !l.managed);
  const managed = original.locations.filter((l) => l.managed);
  if (managed.length) lines.push(`The original is deleted from ${managed.map((l) => l.storageName).join(', ')}.`);
  if (inPlace.length) lines.push(`The original stays in place on ${inPlace.map((l) => l.storageName).join(', ')}; PRISM never deletes files it catalogues in place.`);
  // 1.2.0: further masters (UHD and HD, Textless…) go with the asset.
  const further = asset.representations?.filter((r) => r.kind === 'master').length ?? 0;
  if (further) lines.push(`Its ${further} further master${further === 1 ? ' is' : 's are'} deleted too.`);
  return lines;
}

export function DeleteAssetButton({ asset, onDeleted, size = 'sm' }: { asset: Deletable; onDeleted?: () => void; size?: 'sm' | 'md' }) {
  const me = useMe().data!;
  const [open, setOpen] = useState(false);
  if (!me.permissions.includes('asset.delete') || asset.deleted) return null;
  return (
    <>
      <button className={`btn ghost ${size === 'sm' ? 'sm' : ''}`} onClick={() => setOpen(true)} data-testid="delete-asset" title="Delete asset">
        <Trash2 size={14} /> Delete
      </button>
      {open && <DeleteAssetDialog asset={asset} onClose={() => setOpen(false)} onDeleted={onDeleted} />}
    </>
  );
}

function DeleteAssetDialog({ asset, onClose, onDeleted }: { asset: Deletable; onClose: () => void; onDeleted?: () => void }) {
  const settings = useRecycleSettings().data;
  const { remove, restore } = useDeleteActions();
  const [inUse, setInUse] = useState<Array<{ id: string; name: string }> | null>(null);
  // 1.8.0: the assets made with this one (genealogy); ASSET_IN_USE carries the same count in details.usedIn.
  const genealogy = useGenealogy(asset.id);
  const [usedIn, setUsedIn] = useState<number | null>(null);
  const usedInLine = usedInText(usedIn ?? genealogy.data?.usedInCount);
  const bin = settings?.enabled ?? true;

  async function submit() {
    try {
      const res = await remove.mutateAsync({ id: asset.id, confirmInUse: inUse !== null });
      onClose();
      onDeleted?.();
      toast(res.recycleBin ? `Moved “${asset.title}” to the recycle bin` : `Deleted “${asset.title}”`, 'info', {
        label: 'Undo',
        onClick: () => restore.mutate(asset.id, {
          onSuccess: () => toast(`Restored “${asset.title}”`),
          onError: (e) => toast(errorMessage(e), 'error'),
        }),
      });
    } catch (err) {
      const e = err as ApiError;
      if (e.code === 'ASSET_IN_USE') {
        setInUse((e.details?.sequences as Array<{ id: string; name: string }>) ?? []);
        if (typeof e.details?.usedIn === 'number') setUsedIn(e.details.usedIn);
      } else toast(e.message, 'error');
    }
  }

  return (
    <Modal title={bin ? 'Move to recycle bin?' : 'Delete asset?'} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn danger" onClick={() => void submit()} disabled={remove.isPending} data-testid="confirm-delete">
        {remove.isPending ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} {inUse ? 'Delete anyway' : bin ? 'Move to recycle bin' : 'Delete'}
      </button>
    </>}>
      <p style={{ margin: 0 }}><strong>{asset.title}</strong> leaves the library{bin
        ? <> and waits in the recycle bin for {settings?.retentionDays ?? 30} days. You can restore it until then.</>
        : <>. You can undo this for {Math.round((settings?.undoSeconds ?? 120) / 60) || 1} minute{(settings?.undoSeconds ?? 120) >= 120 ? 's' : ''}; after that it is purged.</>}</p>
      <p className="section-label" style={{ margin: '14px 0 6px' }}>When it is purged</p>
      <ul className="muted" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {consequences(asset).map((l) => <li key={l}>{l}</li>)}
        {usedInLine && <li data-testid="delete-used-in">{usedInLine}</li>}
        <li>The deletion and the purge are recorded in the audit log.</li>
      </ul>
      {inUse && (
        <div className="banner warning" style={{ marginTop: 14 }} role="alert"><div className="banner-body">
          <strong>Used in {inUse.length === 1 ? 'a sequence' : `${inUse.length} sequences`}</strong>
          <span>{inUse.map((s) => s.name).join(', ')}. {asset.mediaKind === 'graphic'
            ? 'Those clips keep rendering the version they use; the graphic is no longer offered for new clips.'
            : 'Those clips stop working once the asset is purged.'}</span>
        </div></div>
      )}
    </Modal>
  );
}

/** Shown on an asset that is waiting in the recycle bin. */
export function DeletedBanner({ asset, onPurged }: { asset: AssetSummary; onPurged?: () => void }) {
  const me = useMe().data!;
  const { restore, purge } = useDeleteActions();
  const usedInLine = usedInText(useGenealogy(asset.deleted ? asset.id : undefined).data?.usedInCount);
  if (!asset.deleted) return null;
  const canDelete = me.permissions.includes('asset.delete');
  return (
    <div className="banner warning" role="status" data-testid="deleted-banner"><div className="banner-body">
      <strong>In the recycle bin</strong>
      <span>Deleted {asset.deleted.by ? `by ${asset.deleted.by} ` : ''}{dateTime(asset.deleted.at)}. It will be purged permanently after {dateTime(asset.deleted.purgeAfter)}.</span>
      {asset.deleted.purgeError && <span style={{ color: 'var(--danger)' }}>{asset.deleted.purgeError}</span>}
      {canDelete && (
        <div className="actions">
          <button className="btn sm" onClick={() => restore.mutate(asset.id, { onSuccess: () => toast(`Restored “${asset.title}”`), onError: (e) => toast(errorMessage(e), 'error') })} data-testid="restore-asset">
            <RotateCcw size={13} /> Restore
          </button>
          <button className="btn ghost sm" onClick={() => {
            if (!window.confirm(`Delete “${asset.title}” permanently? This cannot be undone.${usedInLine ? `\n\n${usedInLine}` : ''}`)) return;
            purge.mutate(asset.id, { onSuccess: () => { toast(`Deleted “${asset.title}” permanently`); onPurged?.(); }, onError: (e) => toast(errorMessage(e), 'error') });
          }}>Delete permanently</button>
          <Link className="btn ghost sm" to="/recycle-bin">Open recycle bin</Link>
        </div>
      )}
    </div></div>
  );
}
